import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { 
  ArrowLeft, 
  MapPin,
  Route,
  Download,
  Copy,
  Settings,
  Navigation,
  Target,
  Trash2
} from 'lucide-react'

const GenerarRutaGPS = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [rutaGenerada, setRutaGenerada] = useState(null)
  const [datos, setDatos] = useState({
    origen_lat: '',
    origen_lng: '',
    destino_lat: '',
    destino_lng: '',
    num_puntos: 20
  })
  
  // Referencias para el mapa
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef([])
  const polylineRef = useRef(null)

  // Cargar Leaflet dinámicamente
  useEffect(() => {
    const loadLeaflet = async () => {
      // Cargar CSS de Leaflet
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
      document.head.appendChild(link)

      // Cargar JS de Leaflet
      const script = document.createElement('script')
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
      
      script.onload = () => {
        initMap()
      }
      
      script.onerror = () => {
        toast.error('Error al cargar Leaflet. Verifica tu conexión a internet.')
      }
      
      document.head.appendChild(script)
    }

    loadLeaflet()

    return () => {
      // Limpiar mapa al desmontar
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
      }
    }
  }, [])

  const initMap = () => {
    if (!window.L) return

    // Inicializar mapa
    const map = window.L.map(mapRef.current).setView([-12.009694, -76.859083], 13)
    
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map)

    // Agregar control de escala
    window.L.control.scale().addTo(map)

    mapInstanceRef.current = map
    markersRef.current = []

    // Manejar clic en el mapa
    map.on('click', (e) => {
      if (markersRef.current.length < 2) {
        const marker = window.L.marker(e.latlng).addTo(map)
        markersRef.current.push(marker)
        
        if (markersRef.current.length === 1) {
          // Primer punto - origen
          setDatos(prev => ({
            ...prev,
            origen_lat: e.latlng.lat.toFixed(6),
            origen_lng: e.latlng.lng.toFixed(6)
          }))
          toast.success('Punto de origen establecido')
        } else {
          // Segundo punto - destino
          setDatos(prev => ({
            ...prev,
            destino_lat: e.latlng.lat.toFixed(6),
            destino_lng: e.latlng.lng.toFixed(6)
          }))
          toast.success('Punto de destino establecido')
        }
      }
    })
  }

  const limpiarMapa = () => {
    if (!mapInstanceRef.current) return

    // Remover marcadores
    markersRef.current.forEach(marker => mapInstanceRef.current.removeLayer(marker))
    markersRef.current = []

    // Remover polyline
    if (polylineRef.current) {
      mapInstanceRef.current.removeLayer(polylineRef.current)
      polylineRef.current = null
    }

    // Limpiar datos
    setDatos(prev => ({
      ...prev,
      origen_lat: '',
      origen_lng: '',
      destino_lat: '',
      destino_lng: ''
    }))

    setRutaGenerada(null)
    toast.success('Mapa limpiado')
  }

  const generarRutaOSRM = async (origen, destino, numPuntos) => {
    // Validar que origen y destino no sean el mismo punto
    if (origen.lat === destino.lat && origen.lng === destino.lng) {
      throw new Error('El punto de origen y destino no pueden ser el mismo')
    }

    // Validar que las coordenadas estén en rangos válidos
    if (origen.lat < -90 || origen.lat > 90 || origen.lng < -180 || origen.lng > 180) {
      throw new Error('Las coordenadas de origen están fuera de rango válido')
    }
    if (destino.lat < -90 || destino.lat > 90 || destino.lng < -180 || destino.lng > 180) {
      throw new Error('Las coordenadas de destino están fuera de rango válido')
    }

    const url = `https://router.project-osrm.org/route/v1/driving/${origen.lng},${origen.lat};${destino.lng},${destino.lat}?overview=full&geometries=geojson&alternatives=false&steps=true&annotations=true&continue_straight=true&generate_hints=false&approaches=curb;curb`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (!data.routes || !data.routes[0]) {
        throw new Error('No se pudo generar una ruta válida')
      }

      const coordinates = data.routes[0].geometry.coordinates
      const speeds = data.routes[0].legs?.[0]?.annotation?.speed || []

      if (!coordinates || coordinates.length === 0) {
        throw new Error('La ruta generada no contiene puntos válidos')
      }

      // Verificar que la ruta no sea un solo punto (mismo origen y destino)
      if (coordinates.length === 1 || 
          (coordinates.length === 2 && 
           coordinates[0][0] === coordinates[1][0] && 
           coordinates[0][1] === coordinates[1][1])) {
        throw new Error('No se puede generar una ruta entre el mismo punto')
      }

      // Validar que todas las coordenadas sean válidas
      const validCoordinates = coordinates.filter(coord => {
        if (!Array.isArray(coord) || coord.length < 2) return false
        const lng = parseFloat(coord[0])
        const lat = parseFloat(coord[1])
        return !isNaN(lng) && !isNaN(lat) && 
               lat >= -90 && lat <= 90 && 
               lng >= -180 && lng <= 180
      })

      if (validCoordinates.length < 2) {
        throw new Error('No se encontraron suficientes coordenadas válidas en la ruta')
      }

      // Distribuir puntos uniformemente
      const puntos = []
      const totalPoints = validCoordinates.length
      const step = totalPoints / (numPuntos - 1)

      // Siempre incluir el primer punto
      if (validCoordinates[0] && validCoordinates[0].length >= 2) {
        const lat = parseFloat(validCoordinates[0][1])
        const lng = parseFloat(validCoordinates[0][0])
        puntos.push({
          latitud: lat,
          longitud: lng,
          velocidad: speeds[0] || 40,
          evento: 'normal'
        })
      }

      // Distribuir puntos intermedios
      for (let i = 1; i < numPuntos - 1; i++) {
        const index = Math.round(i * step)
        const coord = validCoordinates[index]
        
        if (coord && coord.length >= 2) {
          const lat = parseFloat(coord[1])
          const lng = parseFloat(coord[0])
          puntos.push({
            latitud: lat,
            longitud: lng,
            velocidad: speeds[index] || 40,
            evento: 'normal'
          })
        }
      }

      // Siempre incluir el último punto
      const lastIndex = totalPoints - 1
      const lastCoord = validCoordinates[lastIndex]
      
      if (lastCoord && lastCoord.length >= 2) {
        const lat = parseFloat(lastCoord[1])
        const lng = parseFloat(lastCoord[0])
        puntos.push({
          latitud: lat,
          longitud: lng,
          velocidad: speeds[lastIndex] || 40,
          evento: 'normal'
        })
      }

      // Verificar que tenemos puntos válidos
      if (puntos.length === 0) {
        throw new Error('No se pudieron generar puntos válidos para la ruta')
      }

      // Convertir a formato string para compatibilidad
      const puntosString = puntos.map(p => `${p.latitud},${p.longitud}`).join(',')

      return {
        puntos: puntosString,
        puntos_array: puntos,
        total_puntos: puntos.length
      }
    } catch (error) {
      console.error('Error generando ruta:', error)
      throw new Error('Error al generar la ruta: ' + error.message)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)

      // Validaciones
      if (!datos.origen_lat || !datos.origen_lng) {
        toast.error('Las coordenadas de origen son requeridas')
        return
      }

      if (!datos.destino_lat || !datos.destino_lng) {
        toast.error('Las coordenadas de destino son requeridas')
        return
      }

      const origen = {
        lat: parseFloat(datos.origen_lat),
        lng: parseFloat(datos.origen_lng)
      }

      const destino = {
        lat: parseFloat(datos.destino_lat),
        lng: parseFloat(datos.destino_lng)
      }

      // Validar que origen y destino no sean el mismo punto
      if (origen.lat === destino.lat && origen.lng === destino.lng) {
        toast.error('El punto de origen y destino no pueden ser el mismo')
        return
      }

      const response = await generarRutaOSRM(origen, destino, datos.num_puntos)
      setRutaGenerada(response)
      toast.success('Ruta generada exitosamente')

      // Dibujar ruta en el mapa
      if (mapInstanceRef.current && window.L) {
        try {
          // Remover polyline anterior si existe
          if (polylineRef.current) {
            mapInstanceRef.current.removeLayer(polylineRef.current)
            polylineRef.current = null
          }

          // Crear nuevo polyline solo si hay más de un punto
          if (response.puntos_array.length > 1) {
            // Validar y limpiar coordenadas
            const coordinates = response.puntos_array
              .filter(punto => {
                // Verificar que las coordenadas sean números válidos
                const lat = parseFloat(punto.latitud)
                const lng = parseFloat(punto.longitud)
                return !isNaN(lat) && !isNaN(lng) && 
                       lat >= -90 && lat <= 90 && 
                       lng >= -180 && lng <= 180
              })
              .map(punto => [
                parseFloat(punto.latitud), 
                parseFloat(punto.longitud)
              ])

            // Verificar que tenemos coordenadas válidas
            if (coordinates.length < 2) {
              toast.warning('No se pudieron generar coordenadas válidas para dibujar la ruta')
              return
            }

            // Crear el polyline con validaciones adicionales
            try {
              polylineRef.current = window.L.polyline(coordinates, {
                color: 'blue',
                weight: 4,
                opacity: 0.8
              })

              // Verificar que el polyline se creó correctamente
              if (polylineRef.current && polylineRef.current.getLatLngs) {
                polylineRef.current.addTo(mapInstanceRef.current)
                
                // Ajustar vista al polyline de forma segura
                try {
                  const bounds = polylineRef.current.getBounds()
                  if (bounds && bounds.isValid && bounds.isValid()) {
                    mapInstanceRef.current.fitBounds(bounds)
                  }
                } catch (boundsError) {
                  console.warn('Error ajustando vista al polyline:', boundsError)
                  // Centrar en el primer punto como fallback
                  if (coordinates.length > 0) {
                    mapInstanceRef.current.setView(coordinates[0], 13)
                  }
                }
              } else {
                throw new Error('El polyline no se creó correctamente')
              }
            } catch (polylineError) {
              console.error('Error creando polyline:', polylineError)
              toast.error('Error al dibujar la ruta en el mapa')
              
              // Intentar mostrar al menos los puntos como marcadores
              coordinates.forEach((coord, index) => {
                try {
                  const marker = window.L.marker(coord).addTo(mapInstanceRef.current)
                  marker.bindPopup(`Punto ${index + 1}: ${coord[0].toFixed(6)}, ${coord[1].toFixed(6)}`)
                } catch (markerError) {
                  console.warn('Error creando marcador:', markerError)
                }
              })
            }
          } else {
            toast.warning('La ruta generada contiene solo un punto')
          }
        } catch (mapError) {
          console.error('Error en el mapa:', mapError)
          toast.error('Error al mostrar la ruta en el mapa')
        }
      }

    } catch (error) {
      console.error('Error generando ruta:', error)
      toast.error(error.message || 'Error al generar la ruta')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setDatos(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const obtenerPosicionActual = (tipo) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (tipo === 'origen') {
            setDatos(prev => ({
              ...prev,
              origen_lat: position.coords.latitude,
              origen_lng: position.coords.longitude
            }))
            toast.success('Posición de origen establecida')
          } else {
            setDatos(prev => ({
              ...prev,
              destino_lat: position.coords.latitude,
              destino_lng: position.coords.longitude
            }))
            toast.success('Posición de destino establecida')
          }
        },
        (error) => {
          console.error('Error obteniendo posición:', error)
          toast.error('No se pudo obtener la posición actual')
        }
      )
    } else {
      toast.error('Geolocalización no soportada en este navegador')
    }
  }

  const establecerPosicionEjemplo = () => {
    setDatos(prev => ({
      ...prev,
      origen_lat: -12.345678,
      origen_lng: -78.901234,
      destino_lat: -12.355678,
      destino_lng: -78.911234
    }))
    toast.success('Posiciones de ejemplo establecidas')
  }

  const copiarRuta = () => {
    if (rutaGenerada) {
      navigator.clipboard.writeText(rutaGenerada.puntos)
      toast.success('Ruta copiada al portapapeles')
    }
  }

  const descargarRuta = () => {
    if (rutaGenerada) {
      const dataStr = JSON.stringify(rutaGenerada.puntos_array, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'ruta_gps.json'
      link.click()
      URL.revokeObjectURL(url)
      toast.success('Ruta descargada')
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Generar Ruta GPS</h1>
          <p className="text-gray-600">Genera rutas automáticas entre dos puntos</p>
        </div>
        <button
          onClick={() => navigate('/simulaciones-gps')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulario */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Route className="w-5 h-5" />
            Configuración de Ruta
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Punto de Origen */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Punto de Origen
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitud
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="origen_lat"
                    value={datos.origen_lat}
                    onChange={handleChange}
                    placeholder="-12.345678"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitud
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="origen_lng"
                    value={datos.origen_lng}
                    onChange={handleChange}
                    placeholder="-78.901234"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => obtenerPosicionActual('origen')}
                className="mt-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
              >
                <Navigation className="w-3 h-3" />
                Usar Posición Actual
              </button>
            </div>

            {/* Punto de Destino */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Punto de Destino
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitud
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="destino_lat"
                    value={datos.destino_lat}
                    onChange={handleChange}
                    placeholder="-12.355678"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitud
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="destino_lng"
                    value={datos.destino_lng}
                    onChange={handleChange}
                    placeholder="-78.911234"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => obtenerPosicionActual('destino')}
                className="mt-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
              >
                <Navigation className="w-3 h-3" />
                Usar Posición Actual
              </button>
            </div>

            {/* Configuración */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configuración
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Puntos
                </label>
                <input
                  type="number"
                  name="num_puntos"
                  value={datos.num_puntos}
                  onChange={handleChange}
                  min="5"
                  max="1000"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Entre 5 y 1000 puntos (recomendado: 20)
                </p>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={establecerPosicionEjemplo}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <MapPin className="w-4 h-4" />
                Ejemplo
              </button>
              <button
                type="button"
                onClick={limpiarMapa}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Limpiar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center justify-center gap-2"
              >
                <Route className="w-4 h-4" />
                {loading ? 'Generando...' : 'Generar Ruta'}
              </button>
            </div>
          </form>
        </div>

        {/* Mapa */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Mapa Interactivo
          </h2>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Haz clic en el mapa para seleccionar los puntos de origen y destino
            </p>
            <div className="flex gap-2 text-xs">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">1er clic = Origen</span>
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded">2do clic = Destino</span>
            </div>
          </div>

          <div 
            ref={mapRef}
            className="w-full h-96 border border-gray-300 rounded-lg"
            style={{ minHeight: '384px' }}
          />
        </div>
      </div>

      {/* Resultado */}
      {rutaGenerada && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Route className="w-5 h-5" />
            Ruta Generada
          </h2>
          
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-2">Información de la Ruta</h3>
              <div className="text-sm text-green-800 space-y-1">
                <p><strong>Total de puntos:</strong> {rutaGenerada.total_puntos}</p>
                <p><strong>Formato:</strong> JSON optimizado para GPS</p>
                <p><strong>Servicio:</strong> OSRM (Open Source Routing Machine)</p>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Puntos de Ruta</h3>
              
              {/* Formato String */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Formato String:</h4>
                <div className="max-h-32 overflow-y-auto">
                  <pre className="text-xs bg-white p-2 rounded border break-all">
                    {rutaGenerada.puntos}
                  </pre>
                </div>
              </div>

              {/* Formato JSON */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Formato JSON:</h4>
                <div className="max-h-40 overflow-y-auto">
                  <pre className="text-xs bg-white p-2 rounded border">
                    {JSON.stringify(rutaGenerada.puntos_array.slice(0, 3), null, 2)}
                    {rutaGenerada.puntos_array.length > 3 && '\n...'}
                  </pre>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={copiarRuta}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copiar
              </button>
              <button
                onClick={descargarRuta}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Descargar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GenerarRutaGPS 